import { useCallback, useEffect, useRef, useState } from "react";
import {
  useChat,
  useLocalParticipant,
  useRoomContext,
  useTrackTranscription,
  useVoiceAssistant,
} from "@livekit/components-react";
import {
  ChatMessage,
  LocalParticipant,
  Participant,
  Track,
  TranscriptionSegment,
} from "livekit-client";
import { useCompatibleVoiceAssistant } from "@/hooks/use-compatible-voice-assistant";
import { removeValue } from "@/utils/storage";

export type UnifiedMessage = {
  id: string;
  text?: string;
  message?: string;
  timestamp: number;
  isSelf: boolean;
  isUser: boolean;
  lastReceivedTime?: number;
  final?: boolean;
  isMarkdown?: boolean;
};

const mergeAndDeduplicateMessages = (
  ...messagesArrays: UnifiedMessage[][]
): UnifiedMessage[] => {
  const unique = new Map<string, UnifiedMessage>();
  const merged = messagesArrays.flat();

  // We use a Map to ensure we get the final message from transcriptions
  merged.forEach((msg) => {
    const existingMessage = unique.get(msg.id);
    if (existingMessage) {
      // If the message already exists, we keep the final one...
      if (msg.final) {
        unique.set(msg.id, msg);
        // ...or the one with the latest timestamp
      } else if (existingMessage.lastReceivedTime && msg.lastReceivedTime) {
        if (existingMessage.lastReceivedTime < msg.lastReceivedTime) {
          unique.set(msg.id, msg);
        }
      }
    } else {
      // If the message does not exist, we add it
      unique.set(msg.id, msg);
    }
  });

  return Array.from(unique.values());
};

export const useMessages = () => {
  const room = useRoomContext();
  const voiceAssistant = useCompatibleVoiceAssistant();
  const { chatMessages } = useChat();
  const { audioTrack: agentAudioTrack } = useVoiceAssistant();
  const agentTranscriptions = useTrackTranscription(agentAudioTrack);
  const localParticipant = useLocalParticipant();
  const localTranscriptions = useTrackTranscription({
    publication: localParticipant.microphoneTrack,
    source: Track.Source.Microphone,
    participant: localParticipant.localParticipant,
  });
  // Conversation history belongs to the current LiveKit room only. Persisting
  // messages in shared browser storage can expose one shopper's conversation
  // to the next shopper using the same browser or storefront preview session.
  const [messages, setMessages] = useState<UnifiedMessage[]>([]);
  const pendingMessagesRef = useRef<Array<{ id: string; text: string }>>([]);

  useEffect(() => {
    removeValue("messages");
    removeValue("transcriptions");
  }, []);

  const addAgentMessage = useCallback(
    (message: { id?: string; text?: string; timestamp?: number }) => {
      if (!message.text) return;

      const incomingMessage: UnifiedMessage = {
        id: message.id || `agent-${Date.now()}`,
        text: message.text,
        message: message.text,
        timestamp: message.timestamp || Date.now(),
        isSelf: false,
        isUser: false,
        final: true,
        isMarkdown: isMarkdown(message.text),
      };

      setMessages((currentMessages) => {
        const nextMessages = mergeAndDeduplicateMessages(currentMessages, [
          incomingMessage,
        ]).sort((a, b) => a.timestamp - b.timestamp);

        return nextMessages;
      });
    },
    [],
  );

  const deliverMessage = useCallback(
    async (message: { id: string; text: string }, agentIdentity: string) => {
      const compatibilityPayload = new TextEncoder().encode(
        JSON.stringify(message),
      );
      const legacyDelivery = () =>
        Promise.allSettled([
          room.localParticipant.sendText(message.text, { topic: "lk.chat" }),
          room.localParticipant.publishData(compatibilityPayload, {
            reliable: true,
            topic: "nowutalk.user_message",
          }),
        ]);

      try {
        const response = await room.localParticipant.performRpc({
          destinationIdentity: agentIdentity,
          method: "user_message",
          payload: JSON.stringify(message),
          responseTimeout: 5000,
        });
        console.info("Text RPC response", response);
      } catch (error) {
        console.error("Text RPC failed", error);
        await legacyDelivery();
        throw error;
      }
    },
    [room],
  );

  useEffect(() => {
    const agentIdentity = voiceAssistant.agent?.identity;
    if (!agentIdentity || pendingMessagesRef.current.length === 0) return;

    // A shopper can type before the LiveKit worker finishes its cold start.
    // Keep those messages locally and deliver them once the agent joins rather
    // than sending them into a transport with no recipient.
    const pendingMessages = pendingMessagesRef.current;
    pendingMessagesRef.current = [];
    pendingMessages.forEach((message) => {
      deliverMessage(message, agentIdentity).catch((error) => {
        console.error("Failed to send queued chat message", error);
      });
    });
  }, [deliverMessage, voiceAssistant.agent?.identity]);

  const sendChat = useCallback(
    (message: string) => {
      const outgoingMessage: UnifiedMessage = {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        text: message,
        message,
        timestamp: Date.now(),
        isSelf: true,
        isUser: true,
        isMarkdown: isMarkdown(message),
      };

      setMessages((currentMessages) => {
        const nextMessages = mergeAndDeduplicateMessages(currentMessages, [
          outgoingMessage,
        ]).sort((a, b) => a.timestamp - b.timestamp);

        return nextMessages;
      });

      const agentIdentity = voiceAssistant.agent?.identity;
      const delivery = { id: outgoingMessage.id, text: message };
      if (!agentIdentity) {
        pendingMessagesRef.current.push(delivery);
        return;
      }

      deliverMessage(delivery, agentIdentity).catch((error) => {
        console.error("Failed to send chat message", error);
      });
    },
    [deliverMessage, voiceAssistant.agent?.identity],
  );

  useEffect(
    () => {
      // Save timestamp of interaction
      const localMessagesUnified: UnifiedMessage[] = segmentsToUnifiedMessages(
        localTranscriptions.segments,
        localParticipant.localParticipant,
      );
      const agentMessagesUnified: UnifiedMessage[] = segmentsToUnifiedMessages(
        agentTranscriptions.segments,
        agentAudioTrack?.participant,
      );
      const chatMessagesUnified: UnifiedMessage[] =
        chatMessagesToUnifiedMessages(
          chatMessages,
          localParticipant.localParticipant,
        );
      const mergedMessages = mergeAndDeduplicateMessages(
        messages,
        localMessagesUnified,
        agentMessagesUnified,
        chatMessagesUnified,
      );
      const sortedMessages = mergedMessages.sort(
        (a, b) => a.timestamp - b.timestamp,
      );

      setMessages(sortedMessages);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chatMessages,
      agentAudioTrack,
      agentTranscriptions.segments,
      localTranscriptions.segments,
      localParticipant.localParticipant,
    ],
  );

  return { messages, sendChat, addAgentMessage };
};

//Helper function to map chat messages from livekit to unified messages
function chatMessagesToUnifiedMessages(
  chatMessages: ChatMessage[],
  participant: Participant | undefined,
): UnifiedMessage[] {
  return chatMessages.map((msg) => ({
    id: msg.id,
    text: msg.message,
    isSelf: participant instanceof LocalParticipant,
    isUser: participant instanceof LocalParticipant,
    timestamp: msg.timestamp,
    message: msg.message,
    isMarkdown: isMarkdown(msg.message),
  }));
}
// Helper function to map transcription segments to unified messages
function segmentsToUnifiedMessages(
  segments: TranscriptionSegment[],
  participant: Participant | undefined,
): UnifiedMessage[] {
  return segments.map((segment) => ({
    id: segment.id,
    text: segment.final ? segment.text : `${segment.text} ...`,
    isSelf: participant instanceof LocalParticipant,
    isUser: participant instanceof LocalParticipant,
    timestamp: segment.firstReceivedTime,
    lastReceivedTime: segment.lastReceivedTime,
    final: segment.final,
    message: segment.text,
    isMarkdown: isMarkdown(segment.text),
  }));
}

function isMarkdown(text: string | undefined): boolean {
  if (!text) return false;

  const markdownPatterns = [
    /^#{1,6} .+/m, // headers
    /\*\*[^*]+\*\*/g, // bold
    /\*[^*]+\*/g, // italic
    /`[^`]+`/g, // inline code
    /```[\s\S]*?```/g, // code blocks
    /^(\*|-|\+|\d+\.)\s.+/m, // lists, using *, -, +, or numbers
    /\[.*?\]\(.*?\)/g, // links
    /!\[.*?\]\(.*?\)/g, // images
    /^> .+/m, // blockquotes
  ];

  return markdownPatterns.some((regex) => regex.test(text));
}
